import React, {useCallback, useEffect, useRef, useState} from "react";
import {SquareArrowOutUpRight} from 'lucide-react';
import {Skeleton} from "antd";
import {AlertTriangle, ChevronLeft, ChevronRight, Flame, Thermometer, ThermometerSun} from "lucide-react";
import {APIv1} from "../../../api";
import "./DeviceTempCarousel.css"
import {Link} from "react-router-dom";

const getTemperatureMeta = (temperature) => {
    if (temperature >= 60) {
        return {
            key: "critical",
            Icon: Flame,
        };
    }

    if (temperature >= 50) {
        return {
            key: "hot",
            Icon: AlertTriangle,
        };
    }

    if (temperature >= 40) {
        return {
            key: "warm",
            Icon: ThermometerSun,
        };
    }

    return {
        key: "normal",
        Icon: Thermometer,
    };
};

const normalizeDeviceTemperature = (device) => {
    const temperature = Number.parseFloat(device.temperature);

    return {
        serialNumber: device.device_serial_number,
        companyName: device.company_name,
        companyPublicName: device.company_public_name,
        marketRegion: device.market_region,
        temperature: Number.isFinite(temperature) ? Math.floor(temperature) : null,
    };
};

const DeviceTemperatureCarousel = ({t}) => {
    const railRef = useRef(null);
    const [devices, setDevices] = useState([]);
    const [userData, setUserData] = useState(() => JSON.parse(localStorage.getItem("user")) || {});
    const [loading, setLoading] = useState(true);

    const fetchDeviceTemperatures = useCallback(async () => {
        if (!userData.token) {
            setLoading(false);
            return;
        }

        setLoading(true);

        try {
            const response = await APIv1.get("/device/hottest/", {
                headers: {
                    Authorization: `Token ${userData.token}`,
                },
            });

            const results = Array.isArray(response.data) ? response.data : [];

            const hottestDevices = results
                .map(normalizeDeviceTemperature)
                .filter((device) => device.serialNumber && device.temperature !== null);
            setDevices(hottestDevices);
        } catch (error) {
            console.error("Error fetching device temperature data:", error);
        } finally {
            setLoading(false);
        }
    }, [userData.token]);

    useEffect(() => {
        fetchDeviceTemperatures();
    }, [fetchDeviceTemperatures]);

    useEffect(() => {
        const items = JSON.parse(localStorage.getItem("user"));

        if (items) {
            setUserData(items);
        }
    }, []);

    const handleCarouselScroll = (direction) => {
        if (!railRef.current) return;

        const cardWidth = railRef.current.querySelector(".analysis__temperature-card")?.offsetWidth ?? 220;
        railRef.current.scrollBy({
            left: direction * (cardWidth + 12),
            behavior: "smooth",
        });
    };

    const skeletonCards = Array.from({length: 4}, (_, index) => (
        <div className="analysis__temperature-card analysis__temperature-card--loading" key={index}>
            <div className="analysis__temperature-info">
                <Skeleton.Button active shape="circle" style={{width: 32, height: 32}}/>
                <div className="analysis__temperature-device">
                    <Skeleton.Input active style={{width: 84, height: 12}}/>
                    <Skeleton.Input active style={{width: 112, height: 18}}/>
                </div>
            </div>
            <Skeleton.Input active style={{width: 64, height: 32}}/>
        </div>
    ));

    return (
        <section className="analysis__temperature-panel">
            <div className="analysis__temperature-header">
                <div>
                    <h2>{t("analysis.deviceTemperature.title")}</h2>
                    <span>{t("analysis.deviceTemperature.subtitle")}</span>
                </div>

                <div className="analysis__temperature-actions">
                    <button
                        type="button"
                        className="analysis__temperature-action"
                        onClick={() => handleCarouselScroll(-1)}
                        title={t("analysis.deviceTemperature.actions.previous")}
                        aria-label={t("analysis.deviceTemperature.actions.previous")}
                    >
                        <ChevronLeft aria-hidden="true"/>
                    </button>
                    <button
                        type="button"
                        className="analysis__temperature-action"
                        onClick={() => handleCarouselScroll(1)}
                        title={t("analysis.deviceTemperature.actions.next")}
                        aria-label={t("analysis.deviceTemperature.actions.next")}
                    >
                        <ChevronRight aria-hidden="true"/>
                    </button>
                </div>
            </div>

            <div className="analysis__temperature-carousel" ref={railRef}>
                {loading ? skeletonCards : devices.map((device, index) => {
                    const {key, Icon} = getTemperatureMeta(device.temperature);
                    const isHottest = index === 0 && key === "critical";

                    return (
                        <article
                            className={`analysis__temperature-card analysis__temperature-card--${key}${isHottest ? " analysis__temperature-card--hottest" : ""}`}
                            key={device.serialNumber}
                        >
                            {isHottest && (
                                <div className="analysis__temperature-flames" aria-hidden="true">
                                    <Flame size={28}/>
                                    <Flame size={20}/>
                                    <Flame size={14}/>
                                    <Flame size={22}/>
                                    <Flame size={16}/>
                                </div>
                            )}

                            <Link
                                to={`/device/detail/${device.serialNumber}`}
                                className="analysis__temperature-rank"
                            >
                                <SquareArrowOutUpRight size={12}/>
                            </Link>

                            <div className="analysis__temperature-info">
                                <div className="analysis__temperature-company-row">
                                    <div className="analysis__temperature-icon">
                                        <Icon aria-hidden="true"/>
                                    </div>
                                    <div className="analysis__temperature-company">
                                        <strong>{device.companyName || "-"}</strong>
                                        {device.companyPublicName && (
                                            <span>{device.companyPublicName}</span>
                                        )}
                                    </div>
                                </div>
                                <div className="analysis__temperature-device">
                                    <span>{t("analysis.deviceTemperature.labels.deviceId")}</span>
                                    <strong>{device.serialNumber}</strong>
                                </div>
                            </div>

                            <div className="analysis__temperature-reading">
                                <div className="analysis__temperature-value">
                                    {device.temperature}<sup>&deg;C</sup>
                                </div>
                                {device.marketRegion && (
                                    <span>{device.marketRegion}</span>
                                )}
                            </div>
                        </article>
                    );
                })}

                {!loading && devices.length === 0 && (
                    <div className="analysis__temperature-empty">
                        {t("analysis.deviceTemperature.empty")}
                    </div>
                )}
            </div>
        </section>
    );
};

export default DeviceTemperatureCarousel;
